import { NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventModel, safeEventId } from '@frontend/sports/betting-offer/feature/model';
import { FavouritesConfig, LiveNavigationConfig, SitecoreEventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { CompetitionLogoComponent, CompetitionLogosService } from '@frontend/sports/competition-logos/feature';
import { InlineTooltipComponent } from '@frontend/sports/tooltip-feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { ItemComponent } from '../../common/item/item.component';
import { LiveFavouriteFixturesService } from '../../favourites/live-favourites-fixtures.service';
import { FavouriteLeagueToggleComponent } from '../../favourites/toggle/favourite-league-toggle.component';
import { LeftNavType, NavEventModel, TreeItem, TreeItemType } from '../event-details-common.models';
import { TreeDisplayAfterDirective } from './tree-display-after.directive';
import { TreeEventComponent } from './tree-event.component';

@Component({
    selector: 'ms-tree-item',
    templateUrl: 'tree-item.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        NgIf,
        NgFor,
        FavouriteLeagueToggleComponent,
        ItemComponent,
        InlineTooltipComponent,
        IconCustomComponent,
        CompetitionLogoComponent,
        TreeEventComponent,
        TreeDisplayAfterDirective,
    ],
})
export class TreeItemComponent implements OnChanges {
    @Input() item: TreeItem;
    @Input() hideHeader = false;
    @Output() toggle = new EventEmitter<TreeItem>();
    @Output() toggleMarkets = new EventEmitter<TreeItem>();
    @Output() selectEvent = new EventEmitter<EventModel>();

    placeholderArray: number[];
    shouldShowMarketsToggle: boolean;
    hasFavorites: boolean;
    showPlaceholder: boolean;
    showCompetitionLogo: boolean;
    updatedFavourites: string[] = [];

    get children(): TreeItem[] {
        return this.item.children.filter((child) => !child.hidden);
    }

    get events(): NavEventModel[] {
        return this.item.events.filter((event) => !event.hidden);
    }

    constructor(
        public sitecoreEventDetails: SitecoreEventDetailsConfig,
        public liveNavConfig: LiveNavigationConfig,
        private liveFavouriteFixturesService: LiveFavouriteFixturesService,
        private favouritesConfig: FavouritesConfig,
        private competitionLogoService: CompetitionLogosService,
    ) {
        if (this.favouritesConfig.isLiveFavouritesEnabled) {
            this.liveFavouriteFixturesService.change.pipe(takeUntilDestroyed()).subscribe(() => {
                this.updatedFavourites = this.liveFavouriteFixturesService.fixtures.map(({ id }) => id);
            });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.showCompetitionLogo =
            this.item.class === 'league' && (this.competitionLogoService.hasLogo(this.item.sportId, this.item.id) || !!this.item?.imageProfile);

        if (changes.item) {
            this.setProperties();
        }
    }

    toggleItem(item: TreeItem): void {
        this.toggle.emit(item);
    }

    onToggleMarkets(mouseEvent: MouseEvent | undefined, item: TreeItem): void {
        if (mouseEvent) {
            mouseEvent.preventDefault();
            mouseEvent.stopPropagation();
        }
        this.toggleMarkets.emit(item);
    }

    clickItem(mouseEvent: MouseEvent, item: TreeItem): void {
        mouseEvent.preventDefault();
        mouseEvent.stopPropagation();
        this.toggleItem(item);
    }

    trackItemsByFn(index: any, item: TreeItem): number {
        if (item) {
            return item.id;
        }

        return index;
    }

    trackEventsByFn(index: any, event: NavEventModel): string | number {
        if (event && event.data) {
            return event.data.id;
        }

        return index;
    }

    onSelectEvent(eventModel: EventModel): void {
        this.selectEvent.emit(eventModel);
    }

    htmlEventId(event: EventModel): string {
        return `event-${safeEventId(event.id)}`;
    }

    private setProperties(): void {
        this.shouldShowMarketsToggle =
            !this.item.hideToggleMarkets &&
            (this.item.type === TreeItemType.Region || this.item.type === TreeItemType.Sport || this.item.type === TreeItemType.Favourite) &&
            !!this.item.count &&
            this.item.expanded;

        this.hasFavorites = !this.item.hideFavorites && this.item.leftNavType === LeftNavType.Prematch && this.item.type === TreeItemType.League;
        this.showPlaceholder = this.item.expanded && this.item.loading;

        if (this.showPlaceholder) {
            this.placeholderArray = Array.from(Array(this.item.count).keys());
        }
    }

    get loadingIndicatorDelay(): number {
        return this.liveNavConfig.loadingIndicatorDelay;
    }
}
